<script setup>
import BaseDynamicForm from '@/components/base/BaseDynamicForm.vue'

import { getCurrentDay } from '@/utils/DateHelpers'
import { useStore } from '@/utils/VuexHelpers'
import { computed, ref, watch, defineProps, defineEmits } from 'vue'

const props = defineProps({
  edit: {
    type: Boolean,
    default: false
  },
  product: {
    type: Object,
    default: null
  },
  price: {
    type: Object,
    default: null
  },
  customer: {
    type: Object,
    default: null
  },
  allowProductSelect: {
    type: Boolean,
    default: false
  },
  allowCustomerSelect: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['saved'])

const store = useStore()
const loadingProdPrice = computed(() => store.getters.loadingProdPrice)

const dialog = ref(false)
const today = getCurrentDay()

const getFields = () => {
  const priceFields = [
    {
      key: 'price_kg',
      label: 'Price/Kg',
      value: null,
      type: 'number',
      min: 0,
      step: 0.01,
      rules: [(v) => !!v || 'Price is required'],
      placeholder: 'Enter price per kg...'
    },
    {
      key: 'start_date',
      label: 'Effective Date',
      value: today,
      type: 'date',
      rules: [(v) => !!v || 'Effective date is required'],
      placeholder: 'Select effective date...'
    },
    {
      key: 'end_date',
      label: 'End Date',
      value: null,
      clearable: true,
      type: 'date',
      placeholder: 'Select end date...'
    },
    {
      key: 'comment',
      label: 'Comment',
      value: '',
      type: 'text',
      placeholder: 'Enter comment...'
    }
  ]

  if (props.allowProductSelect) {
    return [
      {
        key: 'productlist_id',
        label: 'Product',
        value: props.product || null,
        type: 'product-select',
        rules: [(v) => !!v || 'Product is required']
      },
      ...priceFields
    ]
  }

  if (props.allowCustomerSelect) {
    return [
      {
        key: 'customer_id',
        label: 'Customer',
        value: props.customer || null,
        type: 'customer-select',
        rules: [
          (v) => {
            if (Array.isArray(v)) return v.length > 0 || 'Customer is required'
            return !!v || 'Customer is required'
          }
        ]
      },
      ...priceFields
    ]
  }

  return priceFields
}

const fields = ref(getFields())

const formHeader = computed(() => {
  return props.edit ? `Update Price` : `Create Price`
})

const isLoading = computed(() => {
  if (props.edit) return loadingProdPrice.value === `update-${props.price.id}`

  return loadingProdPrice.value === 'create'
})

const actionName = computed(() => {
  return props.edit ? 'updateProdPrice' : 'createProdPrice'
})

const resetFields = () => {
  fields.value = fields.value.map((field) => ({
    ...field,
    value: getInitialFieldValue(field)
  }))
}

const getInitialFieldValue = (field) => {
  if (!props.edit || !props.price) {
    return field.value
  }

  return props.price[field.key]
}

const buildPricePayload = (formData) => {
  const productlist_id = props.product
    ? props.product.id
    : formData.productlist_id?.id

  const customer_id = props.customer ? props.customer.id : formData.customer_id

  const payload = {
    ...formData,
    productlist_id,
    customer_id,
    price_kg: formData.price_kg === '' ? null : Number(formData.price_kg),
    end_date: formData.end_date || null
  }

  if (props.edit) {
    payload.id = props.price.id
  }

  return payload
}

const saveProductPrice = async (payload) => {
  return store.dispatch(actionName.value, {
    payload,
    productName: props.product?.product
  })
}

const handleFormSubmit = async (formData) => {
  const payload = buildPricePayload(formData)
  const res = await saveProductPrice(payload)

  if (res === 'error') return

  dialog.value = false
  if (!props.edit) {
    resetFields()
  }

  emit('saved')
}

watch(
  () => dialog.value,
  (newState) => {
    if (!props.edit) return
    if (!newState) return

    resetFields()
  },
  {
    immediate: false
  }
)
</script>

<template>
  <v-dialog v-model="dialog" scrollable max-width="500px">
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-if="edit" x-small text color="info" v-bind="attrs" v-on="on">
        <font-awesome-icon icon="fa-solid fa-edit" class="mr-1" />
        Edit Price
      </v-btn>

      <v-btn v-else x-small color="primary" v-bind="attrs" v-on="on">
        <v-icon left small>mdi-plus</v-icon>
        New Price
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-start flex-column">
          <h6
            class="text-button text-light-blue text-start mb-0"
            style="line-height: 1.5rem"
          >
            {{ formHeader }}
          </h6>
          <div
            v-if="product?.product"
            class="text-body-2 grey--text text--darken-1"
          >
            Product: <span class="font-italic">{{ product.product }}</span>
          </div>
          <div
            v-if="customer?.company?.name || customer?.name"
            class="text-body-2 grey--text text--darken-1"
          >
            Customer:
            <span class="font-italic">{{
              customer.company?.name || customer.name
            }}</span>
          </div>
        </div>
      </v-card-title>
      <v-divider class="mt-0 mb-1" />
      <v-card-text>
        <BaseDynamicForm
          :formFields="fields"
          :loading="isLoading"
          :resetOnInputChange="true"
          @submit="handleFormSubmit"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
